import * as config from './config';
import Signal from './control-flow/signal';
import CacheByArgumentStrings from './mem/cache-by-argument-strings';
import logging from './logging';
import { Color, CubeRefractionMapping, Material, MeshBasicMaterial, MeshPhongMaterial, MeshStandardMaterial, MixOperation, NoColorSpace, Texture, RepeatWrapping, ColorRepresentation, SRGBColorSpace, LoadingManager, DefaultLoadingManager, MeshPhysicalMaterial, ImageBitmapLoader, CubeTexture, MaterialLoader, } from 'three';
import { Material as MaterialModel } from './domain';
import { gammaCorrect as gc } from './utils/gamma-correct';
import { overloadHandlerFunction } from './control-flow/overload-handler-fuction';
import GLTFMaterialLoader from './view3d/gltf-material-loader';

const log = logging('MaterialFactory');

export const blank = new MeshBasicMaterial(
    {
        colorWrite: false,
        depthTest: false,
        depthWrite: false,
        stencilWrite: false,
        visible: false,
    });

// TODO: move this into the MaterialFactory if we ever want to use a
// non-default loading manager.
const imageBitmapLoader = new ImageBitmapLoader();
imageBitmapLoader.setOptions({ imageOrientation: "flipY" });

const loadImageBitmap = (url: string): Promise<ImageBitmap> => {
    return new Promise((resolve, reject) => {
        imageBitmapLoader.load(
            url,
            resolve,
            null,
            reject,
        );
    })
}

const loadImageDataTexture = (url: string): Texture => {
    const result = new Texture();

    loadImageBitmap(url)
        .then(img => {
            result.image = img;
            result.needsUpdate = true;
        })
        .catch(log.error);

    return result;
};

type ExtensionFn = (...args: unknown[]) => unknown;

/**
 * The factory classes all inherit from the BaseFactory. The factories aren't
 * much more than a namespace for a bunch of extensions.
 */
class BaseFactory {

    extensions: Map<unknown, ExtensionFn>;
    private static instance: unknown;

    constructor() {
        this.extensions = new Map();
    }

    /**
     * Factories are often useful as singletons.
     */
    static getInstance() {
        if(! this.instance){
            this.instance = new this();
        }
        return this.instance;
    }

    /**
     * Subclasses can override the make method and delegate the extension call
     * to super.make() and then do any required caching or post processing.
     */
    make(name: string, ...args: unknown[]) {
        var ext = this.extensions.get(name);
        if(ext == null){
            throw new Error(`This factory doesn't have an extension by the name "${name}"`);
        }
        return ext.apply(this, args);
    }

    /**
     * Add a factory extension. The supplied function must be a full
     * fledged function, not a fat arrow function. The extension will be called
     * bound to the mesh factory instance so it can access the cache.
     */
    extend(name: string, f: ExtensionFn) {
        if(this.extensions.has(name)) {
            throw new Error(`The extend method cannot override the extension named "${name}". Use the override method if you really want to override the existing extension.`);
        }
        this.extensions.set(name, f);
    }

    /**
     * The extend method won't let you add two extensions by the same name, but
     * you can override an extension with this method. The old extension is
     * returned in case you plan to restore it later.
     */
    override(name: string, f: ExtensionFn) {
        var old = this.extensions.get(name);
        this.extensions.set(name, f);
        return old;
    }
}


export class MaterialFactory extends BaseFactory {

    public manager: LoadingManager;
    public textureCube: Texture;

    private textures: CacheByArgumentStrings<Texture>;
    private materials: CacheByArgumentStrings<Material>;

    gltfMaterialLoader: GLTFMaterialLoader;

    jsonMaterialLoader: MaterialLoader;

    // Handler takes two arguments: the material that was just updated, and the
    // material in the GLB file from which it was derived.
    private onMaterialUpdate = new Signal<[updated: Material, source: Material]>(
        'onMaterialUpdate', 'MaterialFactory');

    onStart = new Signal('onStart');
    onLoad = new Signal('onLoad');
    onProgress = new Signal('onProgress');
    onError = new Signal('onError');

    // Public Interface
    // ----------------

    // **new MaterialFactory()**
    //
    // The `MaterialFactory` is designed for a single page global instance. If
    // there are multilple scenes and canvases on a page, they should still be
    // able to share the same global instance. Throughout the examples I've used
    // the variable `mfac` to represent the global instance, but there's nothing
    // special about that name.
    constructor(manager?: LoadingManager) {
        super();
        this.manager = manager ? manager : DefaultLoadingManager;
        this.gltfMaterialLoader = new GLTFMaterialLoader(this.manager);
        this.gltfMaterialLoader.onMaterialUpdate.add(this.onMaterialUpdate.trigger);

        this.jsonMaterialLoader = new MaterialLoader(this.manager);

        overloadHandlerFunction(this.manager, 'onStart', this.onStart.trigger);
        overloadHandlerFunction(this.manager, 'onLoad', this.onLoad.trigger);
        overloadHandlerFunction(this.manager, 'onProgress', this.onProgress.trigger);
        overloadHandlerFunction(this.manager, 'onError', this.onError.trigger);

        this.textures = new CacheByArgumentStrings();
        this.materials = new CacheByArgumentStrings();

        // We're using a global texture cube in the first iteration, so this
        // will move to a more formal mechanism later.
        this._initTextureCube();
    }


    // **make(name, ...args)**
    //
    // This method provides a single point of entry into the material factory.
    // If you want a chrome material, you can call `mfac.make('Chrome')`, or a
    // wood grain would look like `mfac.make('StandardMelamine',
    // 'maps/001.png')`.
    make(name: string, ...args: any[]): Material {
        this.manager.itemStart(name);
        try {
            let material = this.materials.get(name, ...args);
            if (material == null) {
                material = super.make(name, ...args);

                material.name = name;
                this.materials.put(material, name, ...args);
            }
            return material;
        } catch(error) {
            log.debug(error);
            return blank;
        } finally {
            this.manager.itemEnd(name);
        }
    }

    // Makes a basic material from a plain JavaScript object with material and
    // color properties.
    // TODO: Eventually the make method should be fully aware of domain models.
    makefrom(model: MaterialModel): Material {
        // If we have an assetId, then this is loaded from a GLB file.
        if(model.assetId) {
            return this.gltfMaterialLoader.load(model);
        }
        switch (model.type) {
            case 'WindowEnvironment':
                // the extra color parameter would mess up the WindowEnvironment, so we omit it
                return this.make(model.type);
            case 'WallPaint':
            case 'WallExtension':
            case 'GenericMatte':
            case 'ShadelessColor':
                return this.make(model.type, model.tint);
            case null:
                log.debug('MaterialFactory.makefrom() encountered material domain model with null assetId and null type', model);
                return this.gltfMaterialLoader.load({
                    assetId: 'materials/standard-melamine',
                    tint: '#FF00FF'
                });
            default:
                return this.make(model.type, model.color);
        }
    }

    makeTransparent(model: MaterialModel, opacity: number) {
        const original = this.makefrom(model);

        let result = original;
        if(opacity < 1) {
            const cached = this.materials.get(
                "transparent", model.assetId, model.type, model.color,
                String(opacity));
            if(cached) {
                return cached;
            }

            result = original.clone();
            result.transparent = true;
            result.opacity = opacity;

            const handler = (updated: Material, source: Material) => {
                // This handler may run multiple times, but it will only do its
                // job when it's called on the relevant material, i.e. the 
                // updated parameter is equal to the one we just created.
                if(updated == original) {
                    if(source instanceof MeshPhysicalMaterial) {
                        Object.assign(result, source);
                        Object.setPrototypeOf(result, MeshPhysicalMaterial.prototype);
                    }
                    result.copy(source);
                    result.transparent = true;
                    result.opacity = opacity;
                    (result as MeshStandardMaterial).envMap = this.textureCube;
                    result.needsUpdate = true;

                    this.onMaterialUpdate.delete(handler);
                }
            };
            this.onMaterialUpdate.add(handler);

            this.materials.put(
                result, "transparent", model.assetId, model.type, model.color,
                String(opacity));
        }

        return result;
    }

    // **preloadTexture**
    //
    // This is a 'lazy loader' for texture maps. If you know all the texture
    // maps you're going to use on a page, you can call this to preload them
    // during page load so that the global DefaultLoadingManager can track them.
    // In any event this simply caches a texture by name, irrespective of
    // quality level, and returns that texture instance instead of double
    // loading.
    preloadTexture(texName: string) {
        let tex = this.textures.get(texName);
        if (tex == null) {
            tex = loadImageDataTexture(texName);
//            const loader = new TextureLoader();
//            tex = loader.load(
//                texName,
//                () => { /* noop onLoad */ },
//                null, /* progress events no longer supported */
//                (err) => {
//                    log.trace(
//                        `An error occurred while loading '${texName}'`, err);
//                });
            tex.wrapS = tex.wrapT = RepeatWrapping;
            tex.colorSpace = SRGBColorSpace;
            this.textures.put(tex, texName);
        }
        return tex;
    }

    preloadNonColorTexture(texName: string) {
        const tex = this.preloadTexture(texName);
        tex.colorSpace = NoColorSpace;
        return tex;
    }

    // **_initTextureCube()**
    //
    // For now, I think we can use the same texture cube for every reflective
    // material. When we add higher quality levels, we'll probably want to
    // load separate texture cubes for different materials.
    _initTextureCube() {
        if (this.textureCube == null) {
            this.textureCube = this.loadTextureCube(
                `${config.maps}/schadoplatz/32p/`, 'jpg');
        }
    }

    loadTextureCube(path: string, suffix: string) {
        let cube = this.textures.get(path, suffix) as CubeTexture;
        if(cube == null) {
            cube = new CubeTexture();
            const urls = ['px', 'nx', 'py', 'ny', 'pz', 'nz'].map(s => `${path}/${s}.${suffix}`);
            Promise.all(urls.map(loadImageBitmap))
                .then(images => {
                    // The declared type for CubeTexture.prototype.images is
                    // not compatible with ImageBitmap[], but in practice it's
                    // just fine
                    (cube.images as any) = images;
                    cube.needsUpdate = true;
                })
                .catch(log.error);

            this.textures.put(cube, path, suffix)
        }
        cube.colorSpace = NoColorSpace;
        return cube;
    }
}

const mfac = MaterialFactory.getInstance() as MaterialFactory;

// Material Definitions
// --------------------

// Everything below here is a material definition. 

mfac.extend('LightWarm', function() {
    var material = new MeshBasicMaterial({
        color: gc(0xe0d2ba),
    });
    return material;
});

mfac.extend('LightNeutral', function() {
    var material = new MeshBasicMaterial({
        color: gc(0xd6d4d6),
    });
    return material;
});

mfac.extend('LightCool', function() {
    var material = new MeshBasicMaterial({
        color: gc(0xd3d4dc),
    });
    return material;
});


mfac.extend('Chrome', function() {
    var material = new MeshPhongMaterial({
        color: 0xFFFFFF,
        specular: 0xFFFFFF,
        shininess: 100,
        reflectivity: 0.95,
        combine: MixOperation,
        envMap: this.textureCube
    });

    return material;
});

mfac.extend('Brass', function() {
    var material = new MeshPhongMaterial({
        // This is the exact material color we use in Cycles.
        color: gc(0xE4C28C),
        // The material color at 40% value
        specular: gc(0x66573f),
        shininess: 100,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('SatinNickel', function() {
    var material = new MeshPhongMaterial({
        color: gc(0xD0D0D8),
        specular: gc(0x111116),
        shininess: 50,
        reflectivity: 0.18,
        combine: MixOperation,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('Nickel', function() {
    return this.make('SatinNickel');
});

mfac.extend('Bronze', function() {
    var material = new MeshPhongMaterial({
        // Again, this value comes from Cycles material settings
        color: gc(0x0c0804),
        //  specular: 0x543505,
        shininess: 1,
        reflectivity: 0.05,
        combine: MixOperation,
        envMap: this.textureCube
    });

    return material;
});

mfac.extend('MatteGold', function() {
//    var material = new MeshPhongMaterial({
//        // Again, this value comes from Cycles material settings
//        color: gc(0xC6A677),
//        // The material hue at 25% luminosity
//        specular: gc(0x403526),
//        shininess: 50,
//        reflectivity: 0.18,
//        combine: MixOperation,
//        envMap: this.textureCube
//    });
//    return material;
    return this.jsonMaterialLoader.parse({
        "metadata": {
            "version": 4.7,
            "type": "Material",
            "generator": "Material.toJSON"
        },
        "uuid": "57ea48f7-06a2-4359-b8b4-b34de318d6e1",
        "type": "MeshStandardMaterial",
        "name": "matte-gold",
        "color": 13018743, // 0xc6a677
        "roughness": 0.44999998807907104,
        "metalness": 1,
        "emissive": 0,
        "envMapRotation": [ 0, 0, 0, "XYZ" ],
        "envMapIntensity": 1,
        "side": 0,
        "blendColor": 0
    });
});

mfac.extend('GlossBlack', function() {
    var material = new MeshPhongMaterial({
        color: gc(0x101010),
        specular: gc(0x222222),
        shininess: 100,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('MatteBlack', function() {
    return new MeshStandardMaterial({
        color: 0x282828,
        roughness: 50,
    });
});

mfac.extend('MatteAluminum', function() {
    var material = new MeshPhongMaterial({
        color: gc(0xA0A0A0),
        specular: gc(0x282828),
        shininess: 20,
        reflectivity: 0.5,
        combine: MixOperation,
        envMap: this.textureCube
    });
    return material;
});

// This has never been used, and it doesn't work anyway
//mfac.extend('MatteNickel', function() {
//    var material = new MeshPhongMaterial({
//        color: gc(0xD9D2C5),
//        specular: gc(0x3674B1),
//        shininess: 20,
//        reflectivity: 0.05,
//        combine: MixOperation,
//        envMap: this.textureCube
//    });
//    return material;
//});

mfac.extend('Slate', function() {
    var material = new MeshPhongMaterial({
        color: gc(0x303030),
        specular: gc(0x323232),
        shininess: 100,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('White', function() {
    var material = new MeshPhongMaterial({
        color: 0xffffff,
        specular: 0xffffff,
        reflectivity: 0.5,
        shininess: 100,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('Ivory', function() {
    var material = new MeshPhongMaterial({
        color: gc(0xffffd0),
        specular: gc(0xfffff0),
        shininess: 100,
        reflectivity: 0.5,
        envMap: this.textureCube
    });
    return material;
});

mfac.extend('Mirror', function() {
    return this.make('Chrome');
});

mfac.extend('Glass', function() {
    var material = new MeshPhongMaterial({
        color: 0xFFFFFF,
        specular: 0xFFFFFF,
        shininess: 100,
        envMap: this.textureCube,
        transparent: true,
        opacity: 0.2,
    });
    return material;
});

mfac.extend('FrostedGlass', function() {
    var material = new MeshPhongMaterial({
        color: 0xFFFFFF,
        specular: 0xFFFFFF,
        shininess: 100,
        reflectivity: 0.1,
        combine: MixOperation,
        envMap: this.textureCube,
        transparent: true,
        opacity: 0.6,
    });
    return material;
});

mfac.extend('StandardMelamine', function(texName) {
    var tex = this.preloadTexture(texName);
    var material = new MeshPhongMaterial({
        map: tex,
        shininess: 30
    });
    material.userData.materialColormap = texName;

    return material;
});

//mfac.extend('StandardMelamine', function(colormap: string) {
//    let color: string = undefined;
//    if(colormap) {
//        const match = colormap.match(
//            /closet-organizers-images\/models\/colors\/(\d+)\.png$/);
//        if(match) {
//            // Transform the path to the assetId format for the colormap
//            color = `color-maps/${match[1]}`;
//        } else {
//            color = colormap;
//        }
//    }
//    const result = this.gltfMaterialLoader.load({
//        assetId: 'materials/standard-melamine',
//        color,
//    });
//    result.userData.materialColormap = colormap;
//    return result;
//});

mfac.extend('SemiGlossMelamine', function(texName) {
    var tex = this.preloadTexture(texName);
    var material = new MeshPhongMaterial({
        map: tex,
        shininess: 90
    });
    material.userData.materialColormap = texName;
    return material;
});

mfac.extend('GlossMelamine', function(texName: string, tint: number=0xFFFFFF) {
    var tex = this.preloadTexture(texName);
    var material = new MeshPhongMaterial({
        map: tex,
        shininess: 100,
        envMap: this.textureCube,
        reflectivity: 0.05,
        combine: MixOperation,
        color: tint,
    });
    material.userData.materialColormap = texName;
    material.userData.materialTint = tint;
    return material;
});

mfac.extend('Carpet', function(colorCode: string) {
    var color = this.preloadTexture(
        `${config.maps}/flooring/carpet_diffuse_01.jpg`);
    var normal = this.preloadNonColorTexture(
        `${config.maps}/flooring/carpet_normal.jpg`);

    var material = new MeshPhongMaterial({
        color: new Color(colorCode), //MJB
        map: color,
        shininess: 4,
        normalMap: normal
    });
    material.userData.materialTint = colorCode;
    return material;
});

// Note that the `colorCode` is a string, either '01', '02', '03', '04', '05',
// '06', '07', '08', '09', or '10'.
mfac.extend('OriginalFlooring', function(colorCode) {
    var color = this.preloadTexture(
        `${config.maps}/flooring/Floor_Original_diffuse_${colorCode}.jpg`);
    var normal = this.preloadNonColorTexture(
        `${config.maps}/flooring/Floor_Original_normal.jpg`);
    var specular = this.preloadNonColorTexture(
        `${config.maps}/flooring/Floor_Original_specular.jpg`);
    var material = new MeshPhongMaterial({
        //color: gc(0x808080),
        map: color,
        shininess: 70,
        specularMap: specular,
        normalMap: normal,
        envMap: this.textureCube,
        reflectivity: 0.6,
        combine: MixOperation
    });
    material.userData.materialColormap = colorCode;
    return material;
});

mfac.extend('Parquet', function(map: string) {
    var material = null;
    if (!map) {
        map = "01A";
    }
    var mapCategory = map.slice(0, -1);
    var color = this.preloadTexture(
        `${config.maps}/flooring/Floor_Parquet_${map}.jpg`);
    var rough = null;
    if (this.level >= 4) {
        var rough = this.preloadTexture(
            `${config.maps}/flooring/Floor_Parquet_${mapCategory}_rough.jpg`);
    }
    material = new MeshStandardMaterial({
        map: color,
        roughnessMap: rough,
        // Setting the map and its intensity to zero effectively cancels the
        // influence of the scene.environment map
        envMap: this.textureCube,
        envMapIntensity: 0,
    });
    material.userData.materialColormap = map;
    return material;
});

mfac.extend('Herringbone', function(map: string) {
    var material = null;
    if (!map)
        map == "01A";
    var mapCategory = map.slice(0, -1);
    var color = this.preloadTexture(
        `${config.maps}/flooring/Floor_Herringbone_${map}.jpg`);
    var normal = this.preloadNonColorTexture(
        `${config.maps}/flooring/Floor_Herringbone_${mapCategory}_normal.jpg`);
    var height = this.preloadNonColorTexture(
        `${config.maps}/flooring/Floor_Herringbone_${mapCategory}_height.jpg`);
    var rough = this.preloadNonColorTexture(
        `${config.maps}/flooring/Floor_Herringbone_${mapCategory}_rough.jpg`);

    material = new MeshStandardMaterial({
        map: color,
        normalMap: normal,
        bumpMap: height,
        roughnessMap: rough,
        // Setting the map and its intensity to zero effectively cancels the
        // influence of the scene.environment map
        envMap: this.textureCube,
        envMapIntensity: 0,
    });
    material.userData.materialColormap = map;
    return material;
});

mfac.extend('WindowEnvironment', function(path, suffix) {

    if (path == null) path = `${config.maps}/we_original/`;
    if (suffix == null) suffix = 'jpg';

    let env = this.loadTextureCube(path, suffix);
    env.mapping = CubeRefractionMapping;

    let material = new MeshPhongMaterial({
        color: 0xFFFFFF,
        shininess: 100,
        envMap: env,
        refractionRatio: 0.98,
        combine: MixOperation
    });
    return material;
});

mfac.extend('GenericAsset', function(map) {
    var color = this.preloadTexture(map);
    const material = new MeshPhongMaterial({
        color: 0xFFFFFF,
        map: color,
    });
    return material;
});

mfac.extend('LeatherWinter', function() {
    var color = this.preloadTexture(
        `${config.maps}/leathers/Leather_Winter.jpg`);
    var normal = this.preloadNonColorTexture(
        `${config.maps}/leathers/Leather_Normal.jpg`);
    var rough = this.preloadNonColorTexture(
        `${config.maps}/leathers/Leather_Rough.jpg`);
    let material = new MeshStandardMaterial({
        map: color,
        roughnessMap: rough,
        roughness: 1.0,
        normalMap: normal,
        metalness: 0,
        metalnessMap: rough,
        //refractionRatio: 1.5,
        //combine: MixOperation
    });
    return material;
});

mfac.extend('LeatherOyster', function() {
    var color = this.preloadTexture(
        `${config.maps}/leathers/Leather_Oyster.jpg`);
    var normal = this.preloadNonColorTexture(
        `${config.maps}/leathers/Leather_Normal.jpg`);
    var rough = this.preloadNonColorTexture(
        `${config.maps}/leathers/Leather_Rough.jpg`);
    let material = new MeshStandardMaterial({
        map: color,
        roughnessMap: rough,
        roughness: 1.0,
        normalMap: normal,
        metalness: 0,
        metalnessMap: rough,
        //refractionRatio: 1.5,
        //combine: MixOperation
    });
    return material;
});

mfac.extend('LeatherPewter', function() {
    var color = this.preloadTexture(
        `${config.maps}/leathers/Leather_Pewter.jpg`);
    var normal = this.preloadNonColorTexture(
        `${config.maps}/leathers/Leather_Normal.jpg`);
    var rough = this.preloadNonColorTexture(
        `${config.maps}/leathers/Leather_Rough.jpg`);
    let material = new MeshStandardMaterial({
        map: color,
        roughnessMap: rough,
        roughness: 1.0,
        normalMap: normal,
        metalness: 0,
        metalnessMap: rough,
        //refractionRatio: 1.5,
        //combine: MixOperation
    });
    return material;
});

mfac.extend('WallPaint', function(wallColor: ColorRepresentation) {
    if(wallColor == null) wallColor = 0xB0B0B0;
    const
        tint = new Color(wallColor),
        tex = this.preloadTexture("/closet-organizers-images/3D Assets/wall1.png"),
        material = new MeshStandardMaterial({
            color: wallColor,
            map: tex,
            roughness: 1.0,
            metalness: 0.0,
        });

    tex.repeat.set(0.02, 0.02);
    material.userData.materialTint = tint.getHexString();
    return material;
});

mfac.extend('WallExtension', function(wallColor: ColorRepresentation) {
    if(wallColor == null) wallColor = 0xB0B0B0;
    const
        tint = new Color(wallColor),
        tex = this.preloadTexture("/closet-organizers-images/3D Assets/wall_extension.png"),
        material = new MeshStandardMaterial({
            color: tint,
            map: tex,
            roughness: 1.0,
            metalness: 0.0,
        });

    tex.repeat.set(0.02, 0.02);

    return material;
});

mfac.extend('GenericMatte', function(color: ColorRepresentation) {
    const
        tint = new Color(color),
        material = new MeshStandardMaterial({
        color: tint,
        roughness: 1.0,
        metalness: 0.0,
    });

    material.userData.materialTint = tint.getHexString();
    return material;
});

mfac.extend('ShadelessColor', function(color: ColorRepresentation) {
    const
        tint = new Color(color ?? '#000000'),
        material = new MeshBasicMaterial({color: tint});
    material.userData.materialTint = tint.getHexString();
    return material;
});

export default mfac;


